import React, { useEffect, useState } from 'react'
import type { AssetDetailsData, RecordTableProps } from '../../utility/types'
import { formatInputToDollar, formatKey, parseDollarToNumber } from '../../utility/formatting'
import RecordInputRow from './RecordInputRow'
import trashicon from '../../utility/img/trash-remove.png'
import pencilicon from '../../utility/img/edit-buttons.png'
import accepticon from '../../utility/img/accept.png'
import onButton from '../../utility/img/switch.png'
import offButton from '../../utility/img/off-button.png'
import { type BlotterRecord } from '../../pages/File-uploading/utils/types'
import RatingDropdown from './RatingDropDown'
import { END_ALIGN, NUM_FIELDS, RISK_FIELDS } from './utilities/constant'
import { calcTotalMVandQuantity, recalculateMarketValue } from './utilities/formatting'
import InputBox from '../generic-input-box'

const RecordTable = <T extends AssetDetailsData | BlotterRecord>({
  records,
  setRecords,
  showInputRow = false,
  setShowInputRow,
  onRecordRemove,
  headers = [],
  editableFields = [],
  initialRecordState = {} satisfies Partial<T>,
  validateRecord = () => true,
  isDataReady = false,
  showEdit = false,
  showRemove = false,
  setLocalNotional,
  setLocalQuantity,
  showExpandIcon = true,
  isInEditMode,
  setIsInEditMode,
  assetType,
  currency
}: RecordTableProps<T>) => {
  const [editRowId, setEditRowId] = useState<number | null>(null)
  const [expandDetails, setExpandDetails] = useState<boolean>(false)
  const [tempChanges, setTempChanges] = useState<Partial<AssetDetailsData>>({})

  // Add effect to handle edit mode
  useEffect(() => {
    if (editRowId !== null) {
      setIsInEditMode?.(true)
    } else {
      setIsInEditMode?.(false)
    }
  }, [editRowId, setIsInEditMode])

  const getDisplayRecords = () => records.map((record) =>
    record.UniqueBatchId === editRowId ? { ...record, ...tempChanges } : record
  )

  const toggleEditMode = (uniqueBatchId: number) => {
    if (!editRowId) {
      setEditRowId(uniqueBatchId)
      setIsInEditMode?.(true)
    } else if (editRowId !== null && editRowId !== uniqueBatchId) {
      saveChanges(editRowId)
      setEditRowId(uniqueBatchId)
      setIsInEditMode?.(true)
    } else if (editRowId === uniqueBatchId) {
      saveChanges(uniqueBatchId)
      setEditRowId(null)
      setIsInEditMode?.(false)
    }
  }

  const saveChanges = (uniqueBatchId: number) => {
    if (Object.keys(tempChanges).length > 0) {
      setRecords?.((prevRecords) =>
        prevRecords.map((record) =>
          record.UniqueBatchId === uniqueBatchId ? { ...record, ...tempChanges } : record
        )
      )
    }
    setTempChanges({})
  }

  const toggleExpandDetails = () => {
    if (editRowId !== null) return // Prevent expanding details while in edit mode
    setExpandDetails(prev => !prev)
  }

  const handleDropdownChange = (newValue: string, key: keyof (AssetDetailsData | BlotterRecord)) => {
    setTempChanges((prev) => ({ ...prev, [key]: newValue }))
  }

  const discardChanges = () => {
    setTempChanges({})
    setEditRowId(null)
    setIsInEditMode?.(false)
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, uniqueBatchId: number, key: string) => {
    if (e.key === 'Enter') {
      handleNumericInputConfirm(e, uniqueBatchId, key as keyof AssetDetailsData)
    } else if (e.key === 'Escape') {
      discardChanges()
    }
  }

  const handleNumericInputConfirm = (e: React.ChangeEvent<HTMLInputElement> | React.KeyboardEvent<HTMLInputElement>, uniqueBatchId: number, key: string) => {
    const formattedValue = key === 'Quantity' ? formatInputToDollar(e.currentTarget.value, { digit: 0 }) : formatInputToDollar(e.currentTarget.value)
    handleInputChange(formattedValue, uniqueBatchId, key as keyof AssetDetailsData)
    toggleEditMode(uniqueBatchId)
  }

  const handleInputChange = (value: string, uniqueBatchId: number | null, key: keyof AssetDetailsData) => {
    let numericValue: string | number = parseDollarToNumber(value)
    if (key === 'Quantity') {
      numericValue = parseFloat(parseDollarToNumber(value, true))
    }
    if (editRowId === uniqueBatchId) {
      const updatedValue: Partial<AssetDetailsData> & Record<string, number | string | boolean> = { ...tempChanges, [key]: numericValue }

      if (key === 'Quantity' || key === 'TraderPrice') {
        const result = recalculateMarketValue({ records, updatedValue, uniqueBatchId, key, currency })
        if (result?.value != null) {
          updatedValue[result.valueType] = result.value
          const total = result.totalRecalc
          if (setLocalNotional !== undefined) {
            setLocalNotional(total.totalMV)
          }
          if (setLocalQuantity !== undefined) {
            setLocalQuantity(total.totalQuantity)
          }
        }
      }
      setTempChanges(updatedValue)
    }
  }

  const renderCell = (record: T, field: string) => {
    const value = record[field as keyof T]
    if (field === 'BuyOrSell') {
      return String(value)
    }
    if (field in NUM_FIELDS) {
      return formatInputToDollar(String(value), NUM_FIELDS[field])
    }
    return String(value)
  }

  useEffect(() => {
    const totalReset = calcTotalMVandQuantity({ records })
    if (setLocalNotional !== undefined) {
      setLocalNotional(totalReset.totalMV)
    }
    if (setLocalQuantity !== undefined) {
      setLocalQuantity(totalReset.totalQuantity)
    }
  }, [records])

  return (
    <>
    <div className='overflow-x-auto max-h-[50vh]'>
      <table className="min-w-full table-auto">
        <thead className="sticky top-0">
          <tr className="text-xs font-medium bg-white">
            {showRemove && <th className="border sticky left-0 z-10 bg-gray-100">Actions</th>}
            {records && records.length > 0
              ? Object.keys(records[0])
                .filter(key => key !== 'Overall' && key !== 'isRemoved' && key !== 'UniqueBatchId' && key !== 'RiskCalcDividendPriorTerminationPassed')
                .map((key, i) => {
                  if (key.includes('*') && !expandDetails) {
                    return null
                  }
                  return (
                    <th key={i} className={`border sticky bg-gray-100 ${key === 'Edit' ? 'right-0 z-10' : ''}`}>
                      {formatKey(key, assetType)}
                    </th>
                  )
                })
              : headers.map((key, i) => (
                <th key={i} className="border sticky top-0">
                  {formatKey(key)}
                </th>
              ))}
            <th
              className={`border sticky bg-gray-100 cursor-pointer flex flex-col justify-center items-center ${isInEditMode ? 'opacity-50 cursor-not-allowed' : ''}`}
              onClick={toggleExpandDetails}
            >
              Overall
              {showExpandIcon && <span className="mt-2">
                {expandDetails
                  ? (
                    <img src={onButton} alt='On' width={24} height={24} />
                    )
                  : (
                    <img src={offButton} alt='Off' width={24} height={24} />
                    )}
              </span>}
            </th>
            {showEdit && <th className="border sticky right-0 top-0 z-10 bg-gray-100">{editRowId !== null ? 'Save' : 'Edit'}</th>}
          </tr>
        </thead>
        <tbody>
          {getDisplayRecords().filter(record => !record.isRemoved).map((record, index) => (
            <tr key={record.UniqueBatchId as number} className={`whitespace-nowrap ${index % 2 === 0 ? 'bg-white' : 'bg-gray-100'}`}>
              {showRemove && (
                <td className="border text-center sticky left-0">
                  <button
                    disabled={isInEditMode ?? !isDataReady}
                    className={`hover:bg-rose-300 transition duration-150 ease-in-out rounded-full ${(isInEditMode ?? !isDataReady) ? 'opacity-50 cursor-not-allowed' : ''} ${index % 2 === 0 ? 'bg-white' : 'bg-gray-100'}`}
                    onClick={() => { onRecordRemove?.(record.UniqueBatchId as number) } }
                  >
                    <img src={trashicon} alt='Remove' width={24} height={24} />
                  </button>
                </td>
              )}
              {Object.entries(record)
                .filter(([key]) => key !== 'UniqueBatchId' && key !== 'RiskCalcDividendPriorTerminationPassed' && (expandDetails || !key.includes('*')))
                .map(([key, value], i) => {
                  const isEditable = editableFields.includes(key) && editRowId === record.UniqueBatchId
                  const isColoredCountryCell = key === 'Country' && !record.RiskCalcDividendPriorTerminationPassed
                  const isSellCell = key === 'BuyOrSell' && value === 'Sell'
                  const cellClasses = `border text-center min-w-max text-sm 
                  ${isEditable ? 'font-normal text-gray-700 bg-white bg-clip-padding transition ease-in-out' : ''} 
                  ${key.includes('*') || key === 'Overall'
                  ? (value === 'Pass'
                  ? 'bg-green-500 text-white'
                                    : value === 'Warning'
                  ? 'bg-orange-500 text-white'
                                    : value === 'Fail'
                  ? 'bg-red-500 text-white'
                                    : '')
                  : ''} ${isColoredCountryCell ? 'bg-red-200' : ''} ${isSellCell ? 'bg-red-100' : ''}`

                  return (
                    <td key={i} className={cellClasses}>
                      {isEditable
                        ? (
                            RISK_FIELDS.includes(key)
                              ? (
                                  <RatingDropdown
                                    ratingVendor={key}
                                    displayMsg='Select a rating'
                                    value={tempChanges[key as keyof AssetDetailsData] ? String(tempChanges[key as keyof AssetDetailsData]) : String(value)}
                                    onChange={(newValue) => { handleDropdownChange(newValue, key as keyof (AssetDetailsData | BlotterRecord)) } }
                                    disabled={!isDataReady}
                                  />
                                )
                              : (
                                  <InputBox
                                    name={key}
                                    type="text"
                                    value={tempChanges[key as keyof AssetDetailsData] ?? value}
                                    placeholder="Enter value"
                                    isEditable={isEditable}
                                    isNumeric={key === 'TraderPrice'}
                                    isAmount={key === 'Quantity'}
                                    disabled={!isDataReady}
                                    className="w-full text-right rounded-md truncate min-w-[120px] border-gray-300 shadow-sm focus:ring-indigo-200 focus:ring-opacity-50"
                                    onChange={(newValue, fieldName) => { handleInputChange(newValue, record.UniqueBatchId as number, fieldName as keyof AssetDetailsData) }}
                                    onKeyDown={(e, fieldName) => { handleKeyDown(e, record.UniqueBatchId as number, fieldName) }}
                                  />
                                )
                          )
                        : (
                            <div className={`w-full h-full flex ${END_ALIGN.includes(key) ? 'justify-end' : 'justify-start'}`}>
                              {renderCell(record, key)}
                            </div>
                          )}
                    </td>
                  )
                })}
              {showEdit && <td className="border text-center sticky right-0">
                <button
                  disabled={!isDataReady}
                  className={`hover:bg-green-300 transition duration-150 ease-in-out rounded-full ${!isDataReady ? 'opacity-50 cursor-not-allowed' : ''} ${index % 2 === 0 ? 'bg-white' : 'bg-gray-100'}`}
                  onClick={() => { toggleEditMode(record.UniqueBatchId as number) } }
                >
                  {editRowId === record.UniqueBatchId ? <img src={accepticon} alt='Save' width={24} height={24} /> : <img src={pencilicon} alt='Edit' width={24} height={24} />}
                </button>
              </td>}
            </tr>
          ))}
          {showInputRow && (
            <RecordInputRow<T>
              records={records}
              setRecords={setRecords}
              initialRecordState={initialRecordState}
              setShowInputRow={setShowInputRow}
              validateRecord={validateRecord} />
          )}
        </tbody>
      </table>
    </div>
    </>
  )
}

export default RecordTable
